import { Handle, Position, type NodeProps } from 'reactflow'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Mail, Upload, Eye } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import type { EnvioEmailData } from '../../types'
import * as XLSX from 'xlsx'
import type { ContactoRow } from '../../types'
import { NodeDeleteButton } from './NodeDeleteButton'

interface EnvioEmailNodeProps extends NodeProps {
  data: EnvioEmailData & {
    esNodoInicial?: boolean
    onContactosChange?: (contactos: ContactoRow[]) => void
    onChange?: (data: Partial<EnvioEmailData>) => void
    onDelete?: () => void
  }
}

export function EnvioEmailNode({ data }: EnvioEmailNodeProps) {
  const [fileName, setFileName] = useState('')

  function handleExcel(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target?.result, { type: 'binary' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows: ContactoRow[] = XLSX.utils.sheet_to_json(ws)
      data.onContactosChange?.(rows)
    }
    reader.readAsBinaryString(file)
  }

  return (
    <div className="w-[300px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-background shadow-sm overflow-hidden">
      <Handle type="target" position={Position.Top} className="!h-2.5 !w-2.5 !bg-blue-500 !border-2 !border-white dark:!border-background" />

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border-b border-blue-500/20">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400">
          <Mail size={13} />
        </span>
        <span className="flex-1 truncate text-xs font-semibold text-blue-700 dark:text-blue-300">
          {data.titulo?.trim() || 'Envío de Email'}
        </span>
        {data.esNodoInicial && (
          <span className="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
            Inicio
          </span>
        )}
        <NodeDeleteButton onDelete={data.onDelete} nombre={data.titulo?.trim() || 'Envío de Email'} />
      </div>

      {/* Body */}
      <div className="space-y-2 p-3">
        <div>
          <Label className="text-[11px] text-gray-500">Título (referencia)</Label>
          <Input
            className="nodrag h-7 text-xs mt-1"
            value={data.titulo || ''}
            onChange={(e) => data.onChange?.({ titulo: e.target.value })}
            placeholder="Ej. Correo bienvenida"
          />
        </div>

        {data.esNodoInicial && (
          <div>
            <Label className="text-[11px] text-gray-500">Base de datos (.xlsx)</Label>
            <label className="nodrag mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-2 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">
              <Upload size={12} />
              {fileName || 'Subir Excel'}
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcel} />
            </label>
          </div>
        )}

        <div>
          <Label className="text-[11px] text-gray-500">Asunto</Label>
          <Input
            className="nodrag h-7 text-xs mt-1"
            value={data.asunto || ''}
            onChange={(e) => data.onChange?.({ asunto: e.target.value })}
            placeholder="Asunto del email"
          />
        </div>

        <div>
          <Label className="text-[11px] text-gray-500">Preencabezado</Label>
          <Input
            className="nodrag h-7 text-xs mt-1"
            value={data.preencabezado || ''}
            onChange={(e) => data.onChange?.({ preencabezado: e.target.value })}
            placeholder="Texto de preview"
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[11px] text-gray-500">HTML del email</Label>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  disabled={!data.html}
                  className="nodrag flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                >
                  <Eye size={11} /> Vista previa
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Vista previa del email</DialogTitle>
                </DialogHeader>
                <div className="border rounded overflow-hidden bg-white">
                  <iframe
                    title="Vista previa del email"
                    sandbox=""
                    srcDoc={data.html || ''}
                    className="w-full h-[70vh] bg-white"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <textarea
            className="nodrag mt-1 h-16 w-full resize-none rounded-md border border-gray-200 dark:border-gray-700 bg-transparent p-1.5 text-xs font-mono"
            value={data.html || ''}
            onChange={(e) => data.onChange?.({ html: e.target.value })}
            placeholder="<html>...</html>"
          />
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!h-2.5 !w-2.5 !bg-blue-500 !border-2 !border-white dark:!border-background" />
    </div>
  )
}
